import { useEffect, type RefObject } from 'react';

/**
 * Фокус внутри панели с формой.
 *
 * Открытая форма забирает фокус на первое поле, `Tab` и `Shift+Tab` ходят по кругу
 * внутри неё, закрытие возвращает фокус туда, откуда панель открыли, — строке таблицы
 * или кнопке «Добавить». Без этого клавиатурный пользователь после `Tab` с последней
 * кнопки уходит в таблицу под панелью и теряет, где находится (разд. 15: «все
 * интерактивные элементы достижимы с клавиатуры»).
 *
 * Пока поверх открыто окно подтверждения (`aria-modal`), круг держит оно: панель
 * свой круг не навязывает.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    const root = ref.current;

    if (!active || root === null) {
      return undefined;
    }

    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Первое поле формы, а не кнопка «Закрыть» в шапке: панель открывают, чтобы
    // заполнять. Область, с которой начинать, помечена `data-focus-start`.
    const start = root.querySelector<HTMLElement>('[data-focus-start]') ?? root;
    const initial = focusables(start)[0];

    (initial ?? root).focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') {
        return;
      }

      const modal = document.querySelector('[aria-modal="true"]');

      if (modal !== null && modal !== root) {
        return;
      }

      const items = focusables(root);
      const first = items[0];
      const last = items[items.length - 1];

      if (first === undefined || last === undefined) {
        event.preventDefault();
        root.focus();

        return;
      }

      const current = document.activeElement;

      if (event.shiftKey && (current === first || !root.contains(current))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (current === last || !root.contains(current))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);

      // Строка, с которой открыли панель, могла исчезнуть после сохранения.
      if (opener !== null && opener.isConnected) {
        opener.focus();
      }
    };
  }, [ref, active]);
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) => !element.closest('[aria-hidden="true"]') && !element.closest('[inert]'),
  );
}
